import React from "react";
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from "@/components/ui/button";
import { FcGoogle } from "react-icons/fc";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

interface GoogleLoginButtonProps {
  mode: 'signin' | 'signup';
  label?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
}

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({ 
  mode,
  label = mode === 'signin' ? "Sign in with Google" : "Sign up with Google",
  onSuccess,
  onError
}) => {
  const { googleSignIn, googleSignUp } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Get user info from Google using the access token
        const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
          headers: {
            Authorization: `Bearer ${tokenResponse.access_token}`,
          },
        });
        
        const userInfo = await userInfoResponse.json();
        
        if (!userInfo.email) {
          throw new Error('Unable to get email from Google account');
        }

        let response;
        if (mode === 'signin') {
          response = await googleSignIn(userInfo.email);
          
          if (response && response.status === "success") {
            toast({
              title: "Welcome back!",
              description: "You have successfully signed in with Google.",
            });
            navigate("/dashboard");
            onSuccess?.();
          }
        } else {
          // For signup, we need both email and name
          const name = userInfo.name || `${userInfo.given_name || ''} ${userInfo.family_name || ''}`.trim();
          
          if (!name) {
            throw new Error('Unable to get name from Google account');
          }
          
          response = await googleSignUp(userInfo.email, name);
          
          if (response && response.status === "success") {
            toast({
              title: "Account created!",
              description: "You have successfully signed up with Google.",
            });
            navigate("/dashboard");
            onSuccess?.();
          }
        }
      } catch (error: any) {
        console.error(`Google ${mode} error:`, error);
        toast({
          title: `Google ${mode} failed`,
          description: error.message || `Failed to ${mode} with Google`,
          variant: "destructive",
        });
        onError?.(error);
      }
    },
    onError: (error) => {
      console.error('Google OAuth error:', error);
      toast({
        title: "Authentication failed",
        description: "Failed to authenticate with Google",
        variant: "destructive",
      });
      onError?.(error);
    },
  });

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full flex items-center justify-center gap-2 bg-white/5 border-white/20 text-neutral-800 hover:text-neutral-900 hover:bg-white/20 transition-all"
      onClick={() => googleLogin()}
    >
      <FcGoogle className="h-5 w-5" />
      <span>{label}</span>
    </Button>
  );
};

export default GoogleLoginButton;
